import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Box, Typography, Button, TextField, Card, CardContent, CardHeader, Pagination, LinearProgress, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel } from '@mui/material';
import { askGPT } from './OpenAIService';
import UploadIcon from '@mui/icons-material/Upload';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist/legacy/build/pdf';
import 'pdfjs-dist/web/pdf_viewer.css';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import mathJaxConfig from './mathJaxConfig';

// Configurer le Worker pour pdf.js
GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const UploadDocument = () => {
  const [file, setFile] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');
  const [pdfPages, setPdfPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [gotoPage, setGotoPage] = useState('');
  const [analysisResults, setAnalysisResults] = useState({ notions: [], exercises: [] });
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizLoading, setQuizLoading] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [correctAnswers, setCorrectAnswers] = useState({});
  const [isProgressing, setIsProgressing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [userNotes, setUserNotes] = useState(''); // Notes utilisateur

  // Définition des états pour l'entraînement
  const [trainingExercise, setTrainingExercise] = useState('');
  const [trainingAnswer, setTrainingAnswer] = useState('');
  const [trainingFeedback, setTrainingFeedback] = useState('');

  const cleanJsonResponse = (response) => {
    try {
      const jsonStartIndex = response.indexOf('{');
      const jsonEndIndex = response.lastIndexOf('}');
      if (jsonStartIndex !== -1 && jsonEndIndex !== -1) {
        let cleanResponse = response.slice(jsonStartIndex, jsonEndIndex + 1);
        cleanResponse = cleanResponse
          .replace(/\\(?!["\\/bfnrt])/g, '') // Supprime les backslashes invalides
          .replace(/[\u0000-\u001F\u007F-\u009F]/g, ''); // Supprime les caractères de contrôle
        return JSON.parse(cleanResponse);
      } else {
        console.error('La réponse n\'est pas un JSON valide :', response);
        return {};
      }
    } catch (error) {
      console.error('Erreur lors du nettoyage de la réponse JSON :', error);
      return {};
    }
  };

  const formatLaTeX = (text) => {
    if (typeof text !== 'string') {
      console.error('Expected a string for LaTeX formatting, but got:', typeof text);
      return JSON.stringify(text);
    }
    return text
      .replace(/\$(.+?)\$/g, '\\($1\\)') // Formules en ligne
      .replace(/\$\$(.+?)\$\$/g, '\\[$1\\]'); // Formules affichées
  };

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setDocumentUrl(URL.createObjectURL(selectedFile));
      setPdfPages([]);
      setAnalysisResults({ notions: [], exercises: [] });
      setCurrentPage(1);
      setIsProgressing(true);
      setProgress(0);
      setUserNotes('');
    }
  };

  const resetUpload = () => {
    setFile(null);
    setDocumentUrl('');
    setPdfPages([]);
    setAnalysisResults({ notions: [], exercises: [] });
    setCurrentPage(1);
    setIsProgressing(false);
    setProgress(0);
    setUserNotes('');
  };

  const loadAndRenderPdf = async () => {
    try {
      if (!documentUrl) return;

      const pdf = await getDocument({ url: documentUrl }).promise;
      const pages = [];

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({ canvasContext: context, viewport }).promise;
        pages.push(canvas.toDataURL());
        setProgress(((i / pdf.numPages) * 100).toFixed(0));
      }

      setPdfPages(pages);
      setIsProgressing(false);
    } catch (error) {
      console.error('Erreur lors du rendu du PDF :', error);
      setIsProgressing(false);
    }
  };

  useEffect(() => {
    if (documentUrl) {
      loadAndRenderPdf();
    }
  }, [documentUrl]);

  const analyzePage = async (pageIndex) => {
    setLoading(true);
    try {
      const pdf = await getDocument({ url: documentUrl }).promise;
      const page = await pdf.getPage(pageIndex);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item) => item.str).join(' ');

      // Concepts
      const conceptsResponse = await askGPT(`
        Présente de façon pédagogique les concepts clés, à partir du texte, pour comprendre le texte et réaliser les exercices du texte suivant, en français, sous forme JSON :
        {
          "concepts": [
            { "title": "Concept 1", "description": "Description de Concept 1" },
            { "title": "Concept 2", "description": "Description de Concept 2" }
          ]
        }
        Texte : ${pageText}
      `);

      const parsedConcepts = cleanJsonResponse(conceptsResponse);
      const notions = parsedConcepts.concepts || [];

      // Exercices
      const exercisesResponse = await askGPT(`
        Présente les sujets d'exercice et les questions qui se posent pour chaque exercice, au format JSON suivant :
        {
          "exercises": [
            { "title": "Exercice 1", "content": "Contenu Exercice 1" },
            { "title": "Exercice 2", "content": "Contenu Exercice 2" }
          ]
        }
        Texte : ${pageText}
      `);

      const exercises = cleanJsonResponse(exercisesResponse).exercises || [];
      setAnalysisResults({ notions, exercises });
    } catch (error) {
      console.error('Erreur lors de l\'analyse de la page :', error);
    } finally {
      setLoading(false);
    }
  };

  const generateQuiz = async () => {
    setQuizLoading(true);
    try {
      const currentNotions = analysisResults.notions.map(notion => notion.description).join(', ');

      const quizResponse = await askGPT(`
        Génère 10 questions pédagogiques, basées sur les notions suivantes sous forme JSON :
        {
          "questions": [
            {
              "question": "Quel est le théorème de Pythagore ?",
              "options": ["a^2 + b^2 = c^2", "E=mc^2", "F=ma"],
              "correctAnswer": "a^2 + b^2 = c^2",
              "explanation": "Le théorème de Pythagore établit la relation entre les côtés d'un triangle rectangle."
            }
          ]
        }
        Notions : ${currentNotions}
      `);

      const parsedQuiz = cleanJsonResponse(quizResponse);
      setQuizQuestions(parsedQuiz.questions || []);
      setSelectedAnswers({});
      setFeedback({});
    } catch (error) {
      console.error('Erreur lors de la génération du quiz :', error);
    } finally {
      setQuizLoading(false);
    }
  };

  const handleAnswerSelect = (questionIndex, selectedOption) => {
    const currentQuestion = quizQuestions[questionIndex];
    const isCorrect = selectedOption === currentQuestion.correctAnswer;
    setSelectedAnswers({ ...selectedAnswers, [questionIndex]: selectedOption });
    setCorrectAnswers({ ...correctAnswers, [questionIndex]: currentQuestion.correctAnswer });
    setFeedback({
      ...feedback,
      [questionIndex]: isCorrect ? `Excellent ! ${currentQuestion.explanation}` : `Try again... ${currentQuestion.explanation}`,
    });
  };

  const handleAnalyzeTraining = async () => {
    if (!trainingExercise || !trainingAnswer) {
      setTrainingFeedback('Veuillez saisir un énoncé et une réponse.');
      return;
    }

    setLoading(true);
    setTrainingFeedback('');
    try {
      const pdf = await getDocument({ url: documentUrl }).promise;
      const page = await pdf.getPage(currentPage);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item) => item.str).join(' ');

      const feedback = await askGPT(`
        Évalue la réponse de l'étudiant de manière constructive, en tenant compte du contexte, et en suivant cette structure :
          1. Identifie les éléments de la réponse qui sont corrects et félicite l'étudiant pour cela. 
          2. En partant de la réponse de l'étudiant, corrige et explique quelle est la réponse attendue et le raisonnement à suivre. 
        Limite ta réponse à 150 mots et utilise un langage simple et motivant pour encourager l'étudiant. Si nécessaire, utilise des formules mathématiques pour illustrer les corrections ou recommandations.
        Retourne uniquement le texte brut avec des paragraphes séparés par deux sauts de ligne.
        Utilise $...$ pour les formules mathématiques en ligne et $$...$$ pour les formules en mode affiché.
        Contexte : ${pageText}
        Énoncé : ${trainingExercise}
        Réponse : ${trainingAnswer}
      `);
      setTrainingFeedback(feedback);

    } catch (error) {
      console.error('Erreur lors de l\'analyse de l\'exercice :', error);
      setTrainingFeedback('Une erreur est survenue lors de l\'analyse. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, value) => {
    setCurrentPage(value);
    setAnalysisResults({ notions: [], exercises: [] });
  };

  const handleGotoPage = () => {
    const pageNumber = parseInt(gotoPage, 10);
    if (!isNaN(pageNumber) && pageNumber > 0 && pageNumber <= pdfPages.length) {
      setCurrentPage(pageNumber);
    } else {
      alert('Numéro de page invalide');
    }
  };

  return (
    <MathJaxContext config={mathJaxConfig}>
      <Box sx={{ padding: 4 }}>

        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Document" />
          <Tab label="Concepts Clés" />
          <Tab label="Exercices" />
          <Tab label="Quiz" />
          <Tab label="Entraînement" />
          <Tab label="Notes Utilisateur" />
        </Tabs>

        <Box sx={{ marginTop: 4 }}>
          {activeTab === 0 && (
            <Box>
              {!file ? (
                <Box sx={{ border: '2px dashed gray', padding: 4, textAlign: 'center', borderRadius: 2 }}>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    id="file-upload"
                  />
                  <label htmlFor="file-upload">
                    <UploadIcon sx={{ fontSize: 40, color: 'gray' }} />
                    <Typography>Déposez votre document PDF ici</Typography>
                  </label>
                </Box>
              ) : (
                <Box>
                  {isProgressing && <LinearProgress sx={{ marginBottom: 2 }} />}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                    <Pagination
                      count={pdfPages.length}
                      page={currentPage}
                      onChange={handleChangePage}
                      variant="outlined"
                      shape="rounded"
                    />
                    <TextField
                      label="Aller à la page"
                      variant="outlined"
                      size="small"
                      value={gotoPage}
                      onChange={(e) => setGotoPage(e.target.value)}
                      sx={{ width: '100px' }}
                    />
                    <Button variant="contained" onClick={handleGotoPage}>Aller</Button>
                  </Box>
                  <Box sx={{ border: '1px solid #ddd', padding: 2 }}>
                    <img
                      src={pdfPages[currentPage - 1]}
                      alt={`Page ${currentPage}`}
                      style={{ width: '100%', marginBottom: '16px' }}
                    />
                  </Box>
                  <Button variant="contained" onClick={() => analyzePage(currentPage)} disabled={loading}>
                    {loading ? 'Analyse en cours...' : 'Analyser cette page'}
                  </Button>
                  <Button variant="outlined" color="secondary" onClick={resetUpload}>
                    Télécharger un autre document
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              {analysisResults.notions.map((notion, index) => (
                <Card key={index} sx={{ marginBottom: 2 }}>
                  <CardHeader title={notion.title || 'Titre manquant'} />
                  <CardContent>
                    <Typography>{notion.description || 'Description manquante'}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              {analysisResults.exercises.map((exercise, index) => (
                <Card key={index} sx={{ marginBottom: 2 }}>
                  <CardHeader title={exercise.title || 'Exercice sans titre'} />
                  <CardContent>
                    <Typography><MathJax>{formatLaTeX(exercise.content || 'Contenu manquant')}</MathJax></Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Générer un quiz basé sur la page analysée
              </Typography>
              <Button
                variant="contained"
                onClick={generateQuiz}
                disabled={quizLoading || analysisResults.notions.length === 0}
                sx={{ marginBottom: 2 }}
              >
                {quizLoading ? 'Génération en cours...' : 'Générer un quiz'}
              </Button>

              {quizQuestions.length > 0 && (
                <Box>
                  {quizQuestions.map((question, index) => (
                    <Card key={index} sx={{ marginBottom: 2 }}>
                      <CardHeader title={`Question ${index + 1}`} />
                      <CardContent>
                        <Typography><MathJax>{formatLaTeX(question.question)}</MathJax></Typography>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">Choisissez une option</FormLabel>
                          <RadioGroup
                            name={`question-${index}`}
                            value={selectedAnswers[index] || ''}
                            onChange={(e) => handleAnswerSelect(index, e.target.value)}
                          >
                            {question.options.map((option, i) => (
                              <FormControlLabel
                                key={i}
                                value={option}
                                control={<Radio />}
                                label={<MathJax>{formatLaTeX(option)}</MathJax>}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                        {feedback[index] && (
                          <Typography variant="body2" sx={{ color: selectedAnswers[index] === correctAnswers[index] ? 'green' : 'red', marginTop: 2 }}>
                            {feedback[index]}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {activeTab === 4 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Entraînez-vous avec vos propres exercices
              </Typography>
              <TextField
                label="Énoncé de l'exercice"
                multiline
                rows={4}
                fullWidth
                value={trainingExercise}
                onChange={(e) => setTrainingExercise(e.target.value)}
                variant="outlined"
                sx={{ marginBottom: 2 }}
              />
              <TextField
                label="Votre réponse"
                multiline
                rows={4}
                fullWidth
                value={trainingAnswer}
                onChange={(e) => setTrainingAnswer(e.target.value)}
                variant="outlined"
                sx={{ marginBottom: 2 }}
              />
              <Button
                variant="contained"
                onClick={handleAnalyzeTraining}
                disabled={loading}
                sx={{ marginBottom: 2 }}
              >
                {loading ? 'Analyse en cours...' : 'Analyser ma réponse'}
              </Button>
              {trainingFeedback && (
                <Box sx={{ marginTop: 4 }}>
                  <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'green' }}>
                    Retour de Aladinn :
                  </Typography>
                  <Typography sx={{ fontSize: '1rem', color: 'green' }}>
                    <MathJax>{trainingFeedback}</MathJax>
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {activeTab === 5 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Saisissez vos notes
              </Typography>
              <TextField
                label="Notes utilisateur"
                multiline
                rows={6}
                fullWidth
                variant="outlined"
                value={userNotes}
                onChange={(e) => setUserNotes(e.target.value)} // Assurez-vous que la valeur des notes est bien suivie
              />
              <Button
                variant="contained"
                color="primary"
                sx={{ marginTop: 2 }}
                onClick={() => alert('Notes sauvegardées.')}
              >
                Sauvegarder
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </MathJaxContext>
  );
};

export default UploadDocument;
